import { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  Check, Loader2, AlertCircle, User, Calendar, Shield,
  FileSignature, CreditCard, ChevronRight, Package, DollarSign
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { API } from "../config/api";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Normalize FastAPI error responses (string OR Pydantic validation list) into a string.
function errToMessage(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
        return field ? `${field}: ${d?.msg || "invalid"}` : (d?.msg || "invalid");
      })
      .join("; ");
  }
  return fallback;
}

const POLE_PRICES = {
  "9'0": 60, "10'0": 60, "10'6": 65, "11'0": 65, "11'6": 65,
  "12'1": 65, "12'7": 70, "13'1": 70, "13'7": 70, "14'1": 70,
  "14'7": 70, "15'1": 85, "15'7": 85, "16'1": 85, "16'5": 85, "16'9": 85,
};
const INSURANCE_PRICE = 10;
function getPolePrice(len) {
  if (POLE_PRICES[len]) return POLE_PRICES[len];
  const m = String(len || '').match(/(\d+)/);
  if (m) { const f = parseInt(m[1]); return f >= 15 ? 85 : f >= 12 ? 70 : f >= 10 ? 65 : 60; }
  return 70;
}

const AGREEMENT_TEXT = `Circus Pole Vault LLC — Pole Vault Rental Agreement & Assumption of Risk

This Pole Vault Rental Agreement ("Agreement") is entered into between Circus Pole Vault LLC ("Lessor") and the undersigned renter ("Renter"), which may be an individual, school, club, or organization.

1. Rental Term & Auto-Renewal. All rentals are month-to-month unless otherwise stated on an invoice. Upon expiration of an invoice period, rentals automatically renew month-to-month at the standard rate until all equipment is returned and received by Circus Pole Vault LLC.

2. Responsibility for Use & Users. The Renter assumes full responsibility for the rented equipment and for ALL persons who use, access, or handle the equipment, including but not limited to athletes, students, coaches, and third parties.

3. Assumption of Risk & Release of Liability. Pole vaulting and related training activities involve inherent risks of serious injury or death. The Renter knowingly and voluntarily assumes all risks associated with the use of any rented equipment. Circus Pole Vault LLC shall NOT be liable for any injury, death, property damage, or loss arising from the use, misuse, or condition of the equipment, to the fullest extent permitted by law.

4. Equipment Responsibility. The Renter is financially responsible for any lost, stolen, destroyed, or damaged poles, regardless of cause.

5. Optional Insurance. If insurance is purchased, coverage is limited solely to damaged or broken poles occurring during normal use. Insurance does not cover loss, theft, misuse, negligence, or improper storage.

6. Pole Exchanges. Pole exchanges are subject to availability and incur a $35 exchange/restocking fee per pole.

7. ESSX Special Orders. Poles specially ordered from ESSX require a minimum two (2) month rental commitment. During this minimum period, the rental may not be terminated or exchanged.

8. Scope of Agreement. This Agreement applies to the equipment listed on any invoice and to any future rentals from Circus Pole Vault LLC, without requiring a separate agreement.

9. Age, Authority & Minors. The undersigned certifies they are at least eighteen (18) years old and legally authorized to sign. For any user under eighteen (18), a parent or legal guardian must execute this Agreement.

10. Governing Law. This Agreement shall be governed by the laws of the state in which Circus Pole Vault LLC operates.

*This contract also covers any other poles owned by Circus Pole Vault that are rented by the customer at a later point.
*If we have to order these poles from ESSX, there is a 2-month minimum rental period before you are able to swap out or terminate the rental agreement.`;


// Signature pad component
function SignaturePad({ onSignatureChange }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const endDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    setHasSignature(true);
    onSignatureChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange('');
  };

  return (
    <div>
      <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white relative">
        <canvas
          ref={canvasRef}
          width={500}
          height={150}
          className="w-full cursor-crosshair touch-none"
          style={{ height: '150px' }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-400 text-sm">
            Sign here
          </div>
        )}
      </div>
      {hasSignature && (
        <Button variant="ghost" size="sm" onClick={clear} className="mt-1 text-xs text-slate-500">
          Clear signature
        </Button>
      )}
    </div>
  );
}


// Card form using Stripe Elements
function CardForm({ requestId, onPaymentMethodReady }) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardComplete, setCardComplete] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.post(`${API}/contracts/setup-intent`, { request_id: requestId })
      .then(res => { setClientSecret(res.data.client_secret); setLoading(false); })
      .catch(() => { toast.error("Failed to initialize payment"); setLoading(false); });
  }, [requestId]);

  useEffect(() => {
    // Store a confirm function that parent can call
    if (stripe && elements && clientSecret && cardComplete) {
      onPaymentMethodReady(async () => {
        const cardElement = elements.getElement(CardElement);
        const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: { card: cardElement }
        });
        if (error) throw new Error(error.message);
        return setupIntent.payment_method;
      });
    } else {
      onPaymentMethodReady(null);
    }
  }, [stripe, elements, clientSecret, cardComplete, onPaymentMethodReady]);

  if (loading) return <div className="flex items-center gap-2 py-4 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" />Loading payment form...</div>;

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white">
      <CardElement 
        options={{
          style: {
            base: { fontSize: '16px', color: '#1e293b', '::placeholder': { color: '#94a3b8' } },
            invalid: { color: '#ef4444' }
          }
        }}
        onChange={(e) => setCardComplete(e.complete)}
      />
    </div>
  );
}


// Main agreement page
function AgreementFormContent() {
  const [searchParams] = useSearchParams();
  const requestId = parseInt(searchParams.get('request') || '0');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [request, setRequest] = useState(null);
  const [step, setStep] = useState('select'); // select, agreement, complete
  const [submitting, setSubmitting] = useState(false);
  
  // Pole selection state
  const [selections, setSelections] = useState({});
  const [includeInsurance, setIncludeInsurance] = useState(true);
  
  // Form state
  const [form, setForm] = useState({
    athlete_name: '',
    athlete_is_minor: false,
    renter_name: '',
    phone: '', email: '',
    agreement_date: new Date().toISOString().split('T')[0],
    authorized_rep: '', signature_date: new Date().toISOString().split('T')[0],
    agree_terms: false
  });
  const [signature, setSignature] = useState('');
  const [confirmPaymentFn, setConfirmPaymentFn] = useState(null);
  const [honeypot, setHoneypot] = useState('');

  useEffect(() => {
    let cancelled = false;
    const loadRequest = async () => {
      if (!requestId) { setError("No request ID"); setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/request/${requestId}`);
        if (cancelled) return;
        const d = res.data;
        if (d?.request) {
          const req = {
            ...d.request, request_id: d.request.id,
            requester_name: d.request.user_name, requester_email: d.request.user_email,
            desired_poles: d.desired_poles || []
          };
          setRequest(req);
          setForm(f => ({ ...f, athlete_name: req.requester_name || '', email: req.requester_email || '', phone: d.request.user_phone || '' }));
          if (d.request.insurance !== undefined) setIncludeInsurance(d.request.insurance);
          
          // Auto-select available poles and check if we can skip step 1
          const initial = {};
          let allAvailable = true;
          (d.desired_poles || []).forEach((dp, idx) => {
            if (dp.exact_available && dp.exact_matches?.[0]) {
              initial[idx] = dp.exact_matches[0].pole_id;
            } else {
              allAvailable = false;
            }
          });
          setSelections(initial);
          if (allAvailable && d.desired_poles?.length > 0) setStep('agreement');
        } else {
          setError("Request not found");
        }
      } catch (err) {
        if (!cancelled) setError(errToMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadRequest();
    return () => { cancelled = true; };
  }, [requestId]);

  const selectedPoles = useMemo(() => {
    if (!request?.desired_poles) return [];
    return request.desired_poles.map((dp, idx) => {
      const pid = selections[idx];
      if (!pid || pid === -1) return null;
      const m = dp.exact_matches?.find(p => p.pole_id === pid) || dp.similar_matches?.find(p => p.pole_id === pid);
      return m ? { ...m, length: m.length || dp.requested?.length, weight: m.weight || dp.requested?.weight } : null;
    }).filter(Boolean);
  }, [request, selections]);

  const poleTotal = selectedPoles.reduce((s, p) => s + getPolePrice(p.length), 0);
  const insTotal = includeInsurance ? selectedPoles.length * INSURANCE_PRICE : 0;
  const monthlyTotal = poleTotal + insTotal;
  const polesText = selectedPoles.map(p => `${p.length} - ${p.weight}`).join(', ');

  const canProceedFromSelect = request?.desired_poles?.every((dp, idx) => 
    dp.exact_available 
    || (selections[idx] !== null && selections[idx] !== undefined)
    || (!dp.similar_matches || dp.similar_matches.length === 0) // no alternatives -> allow proceed
  ) ?? false;
  
  // When the athlete is a minor, the signer must match the named renter (case-insensitive).
  const signerMatchesRenter = (
    !form.athlete_is_minor ||
    (form.authorized_rep || '').trim().toLowerCase() === (form.renter_name || '').trim().toLowerCase()
  );

  const canSubmit = form.agree_terms 
    && form.athlete_name 
    && form.phone && form.email 
    && form.authorized_rep && signature && confirmPaymentFn && !honeypot
    && (!form.athlete_is_minor || form.renter_name) // renter required only if athlete is a minor
    && signerMatchesRenter;

  const handleSubmit = async () => {
    if (honeypot) return; // spam
    if (!canSubmit) {
      if (!signerMatchesRenter) {
        toast.error("Signer name must match the Renter (Coach/Parent/Guardian) for a minor athlete.");
      } else {
        toast.error("Please complete all required fields, sign, and add payment method");
      }
      return;
    }
    setSubmitting(true);
    try {
      // Confirm Stripe payment method first
      let paymentMethodId = null;
      try {
        paymentMethodId = await confirmPaymentFn();
      } catch (err) {
        toast.error(`Payment error: ${err.message}`);
        setSubmitting(false);
        return;
      }
      
      // Submit contract
      await axios.post(`${API}/contracts/submit`, {
        request_id: requestId,
        athlete_name: form.athlete_name,
        athlete_is_minor: form.athlete_is_minor,
        renter_name: form.renter_name,
        phone: form.phone,
        email: form.email,
        agreement_date: form.agreement_date,
        authorized_rep: form.authorized_rep,
        signature_date: form.signature_date,
        signature_data: signature,
        stripe_payment_method_id: paymentMethodId,
        agree_terms: form.agree_terms
      });
      
      toast.success("Contract signed successfully!");
      setStep('complete');
    } catch (err) {
      toast.error(errToMessage(err, "Failed to submit contract"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-12 h-12 animate-spin text-slate-400" /></div>;
  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full"><CardContent className="pt-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold mb-2">Error</h2><p className="text-slate-600">{error}</p>
      </CardContent></Card>
    </div>
  );

  if (step === 'complete') return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full"><CardContent className="pt-8 text-center">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-emerald-600" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">All Done!</h2>
        <p className="text-slate-600 mb-4">Your contract has been signed and payment method added. We&apos;ll be in touch about your pole pickup!</p>
        <p className="text-sm text-slate-500">You can close this page.</p>
      </CardContent></Card>
    </div>
  );

  // Step 1: Pole selection (skipped if all available)
  if (step === 'select') return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 justify-center mb-6 text-sm">
          <Badge className="bg-blue-100 text-blue-700">1. Select Poles</Badge>
          <ChevronRight className="w-4 h-4 text-slate-300" />
          <Badge variant="outline" className="text-slate-400">2. Sign & Pay</Badge>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-6">Select Your Poles</h1>
        <div className="space-y-4">
          {request?.desired_poles?.map((dp, idx) => (
            <Card key={idx}>
              <CardHeader><CardTitle className="text-base flex justify-between">
                <span>Requested: {dp.length || dp.requested?.length} - {dp.weight || dp.requested?.weight}</span>
                {dp.exact_available ? <Badge className="bg-emerald-500">Available</Badge> : <Badge variant="secondary">Alternatives</Badge>}
              </CardTitle></CardHeader>
              <CardContent>
                {dp.exact_available ? (
                  <div className="text-sm text-emerald-700 bg-emerald-50 p-3 rounded-md"><Check className="w-4 h-4 inline mr-2" />Available!</div>
                ) : dp.similar_matches?.length > 0 ? (
                  <RadioGroup value={selections[idx]?.toString() || ''} onValueChange={(v) => setSelections(p => ({ ...p, [idx]: parseInt(v) }))}>
                    {dp.similar_matches.map((pole, i) => (
                      <div key={i} className={`flex items-center space-x-3 p-3 rounded-md border ${selections[idx] === pole.pole_id ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}>
                        <RadioGroupItem value={pole.pole_id.toString()} id={`p-${idx}-${i}`} />
                        <Label htmlFor={`p-${idx}-${i}`} className="flex-1 cursor-pointer"><span className="font-semibold">{pole.length} - {pole.weight}</span> <span className="text-slate-500">({pole.brand})</span></Label>
                      </div>
                    ))}
                  </RadioGroup>
                ) : <div className="text-sm text-amber-700 bg-amber-50 p-3 rounded-md"><AlertCircle className="w-4 h-4 inline mr-2" />No alternatives currently in stock. We&apos;ll source one for you — proceed to sign and we&apos;ll be in touch.</div>}
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="mt-8 flex justify-center">
          <Button size="lg" onClick={() => setStep('agreement')} disabled={!canProceedFromSelect} className="min-w-[240px]">
            Continue to Agreement <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );

  // Step 2: Agreement + Signature + Payment (all on one page)
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 justify-center mb-6 text-sm">
          <Badge variant="outline" className="text-emerald-600 border-emerald-200"><Check className="w-3 h-3 inline mr-1" />Poles Selected</Badge>
          <ChevronRight className="w-4 h-4 text-slate-300" />
          <Badge className="bg-blue-100 text-blue-700">2. Sign & Pay</Badge>
        </div>

        <h1 className="text-2xl font-bold text-slate-900 text-center mb-2">Rental Agreement</h1>
        <p className="text-center text-slate-500 mb-6">Request #{requestId} - {request?.requester_name}</p>

        {/* Rental Summary */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-4 h-4 text-slate-500" />
              <span className="font-semibold text-sm">Rental Summary</span>
            </div>
            <div className="text-sm space-y-1">
              {selectedPoles.map((p, i) => (
                <div key={i} className="flex justify-between text-slate-600">
                  <span>{p.length} - {p.weight}</span><span>${getPolePrice(p.length).toFixed(2)}/mo</span>
                </div>
              ))}
              {includeInsurance && <div className="flex justify-between text-slate-600"><span>Insurance ({selectedPoles.length}x)</span><span>${insTotal.toFixed(2)}/mo</span></div>}
              <div className="border-t pt-1 flex justify-between font-semibold"><span>Monthly Total</span><span>${monthlyTotal.toFixed(2)}</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Agreement Text */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <FileSignature className="w-4 h-4 text-slate-500" />
              Rental Agreement & Assumption of Risk
            </h3>
            <div className="bg-slate-50 rounded-lg p-4 max-h-[300px] overflow-y-auto text-xs text-slate-700 whitespace-pre-line leading-relaxed border">
              {AGREEMENT_TEXT}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Checkbox id="agree" checked={form.agree_terms} onCheckedChange={(v) => setForm(f => ({ ...f, agree_terms: v }))} />
              <Label htmlFor="agree" className="text-sm cursor-pointer">
                I agree to the above terms and conditions <span className="text-red-500">*</span>
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Renter Information */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <User className="w-4 h-4 text-slate-500" />
              Renter Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Athlete&apos;s Name <span className="text-red-500">*</span></Label>
                <Input 
                  data-testid="athlete-name-input"
                  value={form.athlete_name} 
                  onChange={(e) => setForm(f => ({ ...f, athlete_name: e.target.value }))} 
                  placeholder="Person using the pole" 
                />
              </div>
              <div>
                <Label className="text-xs">Phone <span className="text-red-500">*</span></Label>
                <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(000) 000-0000" />
              </div>
              <div>
                <Label className="text-xs">Email <span className="text-red-500">*</span></Label>
                <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@example.com" />
              </div>
              <div>
                <Label className="text-xs">Agreement Date</Label>
                <Input type="date" value={form.agreement_date} onChange={(e) => setForm(f => ({ ...f, agreement_date: e.target.value }))} />
              </div>
            </div>

            {/* Minor toggle + conditional Renter (coach/parent/guardian) field */}
            <div className="mt-4 bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="flex items-center gap-2">
                <Checkbox 
                  data-testid="athlete-is-minor-checkbox"
                  id="athlete-is-minor" 
                  checked={form.athlete_is_minor} 
                  onCheckedChange={(v) => setForm(f => ({ ...f, athlete_is_minor: !!v, renter_name: v ? f.renter_name : '' }))} 
                />
                <Label htmlFor="athlete-is-minor" className="text-sm cursor-pointer">
                  Athlete is a minor (under 18)
                </Label>
              </div>
              {form.athlete_is_minor && (
                <div className="mt-3">
                  <Label className="text-xs">
                    Renter&apos;s Name — Coach / Parent / Guardian <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    data-testid="renter-name-input"
                    value={form.renter_name} 
                    onChange={(e) => setForm(f => ({ ...f, renter_name: e.target.value }))} 
                    placeholder="Adult responsible for the minor athlete" 
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Because the athlete is a minor, an adult must be named as the renter.
                  </p>
                </div>
              )}
            </div>

            {/* Prefilled read-only fields */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 bg-slate-50 rounded-lg p-3">
              <div><span className="text-[10px] text-slate-400 uppercase">Poles</span><div className="text-xs font-mono">{polesText || '-'}</div></div>
              <div><span className="text-[10px] text-slate-400 uppercase">Insurance</span><div className="text-xs font-mono">${insTotal.toFixed(2)}/mo</div></div>
              <div><span className="text-[10px] text-slate-400 uppercase">Monthly</span><div className="text-xs font-mono font-semibold">${monthlyTotal.toFixed(2)}</div></div>
              <div><span className="text-[10px] text-slate-400 uppercase">Start Date</span><div className="text-xs font-mono">{request?.rental_start_date ? new Date(request.rental_start_date).toLocaleDateString() : 'TBD'}</div></div>
            </div>
          </CardContent>
        </Card>

        {/* Signature */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-1 flex items-center gap-2">
              <FileSignature className="w-4 h-4 text-slate-500" />
              Signature <span className="text-red-500">*</span>
            </h3>
            <p className="text-xs text-slate-500 mb-3">By signing below, you acknowledge the Circus Pole Vault LLC&apos;s Rental Agreement &amp; checklist.</p>
            <div>
              <Label className="text-xs">Authorized Representative / Parent / Guardian <span className="text-red-500">*</span></Label>
              <Input
                value={form.authorized_rep}
                onChange={(e) => setForm(f => ({ ...f, authorized_rep: e.target.value }))}
                placeholder={form.athlete_is_minor && form.renter_name ? form.renter_name : "Printed name"}
                className={`mb-1 ${form.athlete_is_minor && form.authorized_rep && !signerMatchesRenter ? 'border-red-400' : ''}`}
              />
              {form.athlete_is_minor && (
                <p className={`text-[11px] mb-3 ${form.authorized_rep && !signerMatchesRenter ? 'text-red-600' : 'text-slate-500'}`} data-testid="rental-signer-match-hint">
                  {form.authorized_rep && !signerMatchesRenter
                    ? `Must match the renter's name above ("${form.renter_name}"). Capitalization doesn't matter.`
                    : `Because the athlete is a minor, the signer must be the named renter ("${form.renter_name || '—'}").`}
                </p>
              )}
            </div>
            <SignaturePad onSignatureChange={setSignature} />
            <div className="mt-3">
              <Label className="text-xs">Signature Date</Label>
              <Input type="date" value={form.signature_date} onChange={(e) => setForm(f => ({ ...f, signature_date: e.target.value }))} className="w-48" />
            </div>
          </CardContent>
        </Card>

        {/* Payment Method */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-1 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-slate-500" />
              Payment Method <span className="text-red-500">*</span>
            </h3>
            <p className="text-xs text-slate-500 mb-3">Add a card for your monthly subscription. You won&apos;t be charged now.</p>
            <CardForm requestId={requestId} onPaymentMethodReady={(fn) => setConfirmPaymentFn(() => fn)} />
          </CardContent>
        </Card>

        {/* Honeypot */}
        <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
          <input value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
        </div>

        {/* Submit */}
        <Button size="lg" className="w-full" onClick={handleSubmit} disabled={!canSubmit || submitting}>
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : <><Check className="w-4 h-4 mr-2" />Sign Agreement & Add Payment</>}
        </Button>
        <p className="text-center text-xs text-slate-400 mt-3">Secured by Stripe. Your card details are encrypted.</p>
      </div>
    </div>
  );
}

export default function RentalAgreementPage() {
  return (
    <Elements stripe={stripePromise}>
      <AgreementFormContent />
    </Elements>
  );
}
